// Foreign Accents Bespoke: header menu, drawer, scroll-up header and filter auto-submit.
document.documentElement.classList.add('js');

const desktop = window.matchMedia('(min-width: 1100px)');

/* ---------- Menu panels ----------
   A panel opens on hover after a short intent delay, and stays open for a grace period after
   the pointer leaves, so a diagonal path from the item into its panel keeps it open. The
   chevron button opens it from the keyboard; a first tap on a touch screen opens it too. */
const OPEN_DELAY = 90;
const SWITCH_DELAY = 220;
const CLOSE_DELAY = 320;

const items = [...document.querySelectorAll('[data-nav-item]')];
let openItem = null;
let timer = 0;
let lastPointer = 'mouse';

function setOpen(item, open) {
  const toggle = item.querySelector('.site-nav__toggle');
  const panel = item.querySelector('[data-nav-panel]');
  toggle?.setAttribute('aria-expanded', String(open));
  if (open) {
    panel.style.removeProperty('--panel-shift');
    item.setAttribute('data-open', '');
    // Keep the panel inside the page width: shift it left when it would overflow.
    const rect = panel.getBoundingClientRect();
    const overflow = rect.right - (document.documentElement.clientWidth - 16);
    if (overflow > 0) panel.style.setProperty('--panel-shift', `${-overflow}px`);
  } else {
    item.removeAttribute('data-open');
  }
}

function show(item) {
  clearTimeout(timer);
  if (openItem && openItem !== item) setOpen(openItem, false);
  if (item) setOpen(item, true);
  openItem = item;
}

function hide() {
  clearTimeout(timer);
  if (openItem) setOpen(openItem, false);
  openItem = null;
}

for (const item of items) {
  const link = item.querySelector('.site-nav__link');
  const toggle = item.querySelector('.site-nav__toggle');

  item.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'touch' || !desktop.matches) return;
    clearTimeout(timer);
    if (openItem === item) return;
    timer = setTimeout(() => show(item), openItem ? SWITCH_DELAY : OPEN_DELAY);
  });

  item.addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'touch' || !desktop.matches) return;
    clearTimeout(timer);
    if (openItem) timer = setTimeout(hide, CLOSE_DELAY);
  });

  toggle.addEventListener('click', () => {
    if (openItem === item) hide();
    else show(item);
  });

  link.addEventListener('pointerdown', (event) => { lastPointer = event.pointerType; });
  link.addEventListener('click', (event) => {
    // First tap on a touch screen opens the panel; the second tap follows the link.
    if (lastPointer === 'touch' && openItem !== item) {
      event.preventDefault();
      show(item);
    }
  });

  item.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openItem === item) {
      hide();
      toggle.focus();
    } else if (event.key === 'ArrowDown' && event.target === toggle) {
      event.preventDefault();
      show(item);
      item.querySelector('.nav-panel a')?.focus();
    }
  });

  item.addEventListener('focusout', (event) => {
    if (openItem === item && !item.contains(event.relatedTarget)) hide();
  });
}

document.addEventListener('pointerdown', (event) => {
  if (openItem && !openItem.contains(event.target)) hide();
});

/* ---------- Drawer ---------- */

const drawer = document.querySelector('[data-drawer]');
const drawerButton = document.querySelector('[data-drawer-open]');
if (drawer && drawerButton) {
  drawerButton.setAttribute('aria-expanded', 'false');
  drawerButton.addEventListener('click', () => {
    drawer.showModal();
    drawerButton.setAttribute('aria-expanded', 'true');
  });
  drawer.querySelector('[data-drawer-close]')?.addEventListener('click', () => drawer.close());
  drawer.addEventListener('close', () => drawerButton.setAttribute('aria-expanded', 'false'));
  // A click on the backdrop lands on the dialog itself.
  drawer.addEventListener('click', (event) => { if (event.target === drawer) drawer.close(); });
  desktop.addEventListener('change', () => { if (desktop.matches && drawer.open) drawer.close(); });
}

/* ---------- Header hides on scroll down, returns on scroll up ---------- */

const headerSection = document.querySelector('.section-header');
const keepVisible = !!document.querySelector('[data-site-header][data-keep-visible]');
if (headerSection) {
  let lastY = window.scrollY;
  let ticking = false;
  const update = () => {
    const y = window.scrollY;
    // A stuck sticky element reports its stuck position in offsetTop, so measure the header's
    // resting place from the section above it (the announcement bar).
    const above = headerSection.previousElementSibling;
    const restTop = above ? above.offsetTop + above.offsetHeight : 0;
    const threshold = restTop + headerSection.offsetHeight;
    const busy = openItem || drawer?.open || headerSection.contains(document.activeElement);
    headerSection.toggleAttribute('data-scrolled', y > restTop);
    if (y > lastY && y > threshold + 80 && !busy && !keepVisible) headerSection.setAttribute('data-hidden', '');
    else if (y < lastY - 4 || y <= threshold) headerSection.removeAttribute('data-hidden');
    lastY = y;
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
}

/* ---------- Collection filters submit on change ---------- */

for (const form of document.querySelectorAll('[data-facets]')) {
  form.addEventListener('change', (event) => {
    if (event.target.matches('input[type="number"]')) return;
    form.requestSubmit();
  });
  // Price inputs submit when the shopper leaves the field or presses Enter.
  for (const input of form.querySelectorAll('input[type="number"]')) {
    input.addEventListener('blur', () => { if (input.value !== input.defaultValue) form.requestSubmit(); });
  }
  // Only one filter panel stays open at a time; Escape or a click outside closes it.
  const facets = [...form.querySelectorAll('[data-facet]')];
  for (const facet of facets) {
    facet.addEventListener('toggle', () => {
      if (facet.open) for (const other of facets) if (other !== facet) other.open = false;
    });
  }
  document.addEventListener('click', (event) => {
    for (const facet of facets) if (facet.open && !facet.contains(event.target)) facet.open = false;
  });
  form.addEventListener('keydown', (event) => {
    const facet = event.target.closest('[data-facet]');
    if (event.key === 'Escape' && facet?.open) {
      facet.open = false;
      facet.querySelector('summary').focus();
    }
  });
  // Drop empty fields so the URL stays clean.
  form.addEventListener('formdata', (event) => {
    for (const [key, value] of [...event.formData.entries()]) if (value === '') event.formData.delete(key);
  });
}

/* ---------- Announcement messages take turns ---------- */

for (const group of document.querySelectorAll('[data-rotate]')) {
  const messages = [...group.children];
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (messages.length < 2 || reduce.matches) continue;
  let index = Math.max(0, messages.findIndex((m) => !m.hidden));
  let paused = false;
  group.addEventListener('pointerenter', () => { paused = true; });
  group.addEventListener('pointerleave', () => { paused = false; });
  group.addEventListener('focusin', () => { paused = true; });
  group.addEventListener('focusout', () => { paused = false; });
  setInterval(() => {
    if (paused || document.hidden) return;
    messages[index].hidden = true;
    index = (index + 1) % messages.length;
    messages[index].hidden = false;
  }, Number(group.dataset.rotate) * 1000);
}

/* ---------- Video: the player loads when the visitor presses play ---------- */

for (const video of document.querySelectorAll('[data-video]')) {
  const button = video.querySelector('[data-video-play]');
  const template = video.querySelector('[data-video-template]');
  if (!button || !template) continue;
  button.addEventListener('click', () => {
    video.append(template.content.cloneNode(true));
    button.remove();
  });
}

/* ---------- Product cards: mark second photos once they have loaded ---------- */

for (const img of document.querySelectorAll('.card__img--alt')) {
  const mark = () => { if (img.naturalWidth > 0) img.classList.add('is-loaded'); };
  if (img.complete) mark();
  else img.addEventListener('load', mark, { once: true });
}
